import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ClockCounterClockwise,
  Users,
  Plus,
  PencilSimple,
  Trash,
  ArrowRight,
} from "@phosphor-icons/react";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ActionIcon({ action }) {
  if (action === "role.create") return <Plus size={12} weight="bold" className="text-emerald-600" />;
  if (action === "role.update") return <PencilSimple size={12} weight="bold" className="text-blue-600" />;
  if (action === "role.delete") return <Trash size={12} weight="bold" className="text-red-600" />;
  return <ArrowRight size={12} weight="bold" className="text-slate-500" />;
}

function RoleDiff({ before, after }) {
  const beforeSet = new Set(before || []);
  const afterSet = new Set(after || []);
  const added = [...afterSet].filter((r) => !beforeSet.has(r));
  const removed = [...beforeSet].filter((r) => !afterSet.has(r));
  if (added.length === 0 && removed.length === 0) {
    // create: only after — show all as added
    if ((before || []).length === 0 && after && after.length > 0) {
      return (
        <span className="text-[10px] text-emerald-700">+ {after.join(", ")}</span>
      );
    }
    return <span className="text-[10px] text-slate-400">no change</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 text-[10px]">
      {added.map((r) => (
        <span key={"a" + r} className="text-emerald-700">+ {r}</span>
      ))}
      {removed.map((r) => (
        <span key={"r" + r} className="text-red-700">- {r}</span>
      ))}
    </div>
  );
}

export default function AdminAuditWidgets() {
  const [changes, setChanges] = useState([]);
  const [active, setActive] = useState([]);
  const [busyC, setBusyC] = useState(true);
  const [busyA, setBusyA] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/audit/role-changes", { params: { limit: 20 } });
        setChanges(data);
      } finally {
        setBusyC(false);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/admin/audit/active-users", { params: { hours: 24 } });
        setActive(data);
      } finally {
        setBusyA(false);
      }
    })();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-audit-widgets">
      {/* Recent Role Changes */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="widget-role-changes">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <ClockCounterClockwise size={16} weight="duotone" className="text-blue-600" />
          <span className="text-sm font-semibold text-slate-900">Recent Role Changes</span>
          <span className="text-[10px] text-slate-400 ml-auto">Last 20</span>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {busyC && <div className="p-5 text-sm text-slate-400">Loading…</div>}
          {!busyC && changes.length === 0 && (
            <div className="p-5 text-sm text-slate-400">No role changes yet.</div>
          )}
          {changes.map((c) => (
            <div key={c.id} className="px-5 py-3 flex items-start gap-3" data-testid={`change-row-${c.id}`}>
              <div className="mt-1"><ActionIcon action={c.action} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 font-medium truncate">{c.target_email}</div>
                <RoleDiff before={c.before_roles} after={c.after_roles} />
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {c.actor_name || c.actor_email} · {relTime(c.timestamp)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Today */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="widget-active-users">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Users size={16} weight="duotone" className="text-emerald-600" />
          <span className="text-sm font-semibold text-slate-900">Active in Last 24h</span>
          <span className="text-[10px] text-slate-400 ml-auto">{active.length} user{active.length === 1 ? "" : "s"}</span>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {busyA && <div className="p-5 text-sm text-slate-400">Loading…</div>}
          {!busyA && active.length === 0 && (
            <div className="p-5 text-sm text-slate-400">No sessions in the last 24h.</div>
          )}
          {active.map((u) => (
            <div key={u.user_id} className="px-5 py-3 flex items-center gap-3" data-testid={`active-row-${u.user_id}`}>
              {u.picture ? (
                <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold">
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate">{u.name || u.email}</div>
                <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold text-emerald-700">{relTime(u.last_session_at)}</div>
                <div className="text-[10px] text-slate-400">{u.sessions} session{u.sessions === 1 ? "" : "s"}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
